import { IChildProp, IComplexLabelKey } from '../type';
import { parseAndEvaluate } from '../utils';
import {
  DATA_SOURCE,
  DATA_SOURCE_METRICS,
  IDataSource,
  IDataSourceMetrics,
} from '@intelligence/_config/enumeration/dataSource';
import {
  PC_AND_CONSOLE_SEGMENT,
  ISegment,
} from '@intelligence/_config/enumeration/segment';
import {
  CONSOLE_PLATFORM,
  IConsolePlatform,
  MOBILE_PLATFORM,
  IMobilePlatform,
  GAME_PLATFORM,
  IGamePlatform,
  CONSOLE_PLATFORM_ICON,
} from '@intelligence/_config/enumeration/platform';
import {
  DOWNLOAD_BASIC_INFOMATION,
  DOWNLOAD_DATA_CALCULATION_OPTIONS,
  IDownloadBaiscInfomation,
  IDownloadDataCalculationOptions,
  DOWNLOAD_OTHER_OPTIONS,
  IDownloadOtherOptions,
  DOWNLOAD_MAIN_CATEGORY,
  IDownloadMainCategory,
} from '@intelligence/_config/enumeration/download';
import {
  STREAMING_PLATFORM,
  IStreamingPlatform,
} from '@intelligence/_config/enumeration/streaming';
import {
  GRANULARITY,
  IGranularity,
} from '@intelligence/_config/enumeration/granularity';
import { GENRE, IGenre } from '@intelligence/_config/enumeration/genre';
import {
  DATA_CATEGORY,
  IDataCategory,
  GAME_CATEGORY,
  IGameCategory,
} from '@intelligence/_config/enumeration/category';
import { TEnumerationType } from '@intelligence/_config/enumeration/type';

type TSpecialSourceProp =
  | IDataSourceMetrics
  | ISegment
  | IConsolePlatform
  | IDataSource
  | IDownloadBaiscInfomation
  | IStreamingPlatform
  | IDownloadDataCalculationOptions
  | IGranularity
  | IDownloadOtherOptions
  | IMobilePlatform
  | IDownloadMainCategory
  | IGenre
  | IDataCategory
  | IGameCategory
  | IGamePlatform;

const SPECIAL_SOURCE_MAP: Record<TEnumerationType, TSpecialSourceProp> = {
  dataSource: DATA_SOURCE,
  metric: DATA_SOURCE_METRICS,
  segment: PC_AND_CONSOLE_SEGMENT,
  platformPC: CONSOLE_PLATFORM,
  platformPCIcon: CONSOLE_PLATFORM_ICON,
  platformMobile: MOBILE_PLATFORM,
  platformGame: GAME_PLATFORM,
  downloadBasicInfo: DOWNLOAD_BASIC_INFOMATION,
  downloadDataCalcu: DOWNLOAD_DATA_CALCULATION_OPTIONS,
  downloadOther: DOWNLOAD_OTHER_OPTIONS,
  platformStreaming: STREAMING_PLATFORM,
  granularity: GRANULARITY,
  downloadMainCategory: DOWNLOAD_MAIN_CATEGORY,
  genre: GENRE,
  gameCategory: GAME_CATEGORY,
  dataCategory: DATA_CATEGORY,
};

/**
 * 给每个选项添加显示和隐藏的字段
 * 注意这里是isShow，没有condition这个字段会默认显示
 * 为什么不设计在表单的config中，因为这个状态需要监听，可能会跟随某个值变化去切换显示
 */
export const getAdditionalArr = (
  options: IChildProp[],
  parent: IChildProp,
  formValues: any,
  addFields: any,
): IChildProp[] =>
  options.map((item) => {
    const { condition, children = [] } = item || {};
    // 根据condition字段的条件进行显示隐藏
    const isShow = condition
      ? parseAndEvaluate(condition, {
          ...formValues,
          ...addFields,
        })
      : true;
    return {
      ...item,
      isShow,
      children: children?.length
        ? getAdditionalArr(children, item, formValues, addFields)
        : [],
      parent,
    };
  });

/**
 * 根据给定的条件字符串和上下文计算条件值。
 * 如果条件字符串存在，则解析并评估该条件；否则返回默认值的布尔值。
 *
 * @param condition - 要评估的条件字符串。
 * @param context - 用于评估条件的上下文对象，默认为空对象。
 * @param defaultValue - 当条件字符串为空时返回的默认布尔值。
 * @returns 返回评估后的条件值或默认值。
 */
export const getConditionValue = (
  condition: string,
  context: any = {},
  defaultValue?: boolean,
) => (condition ? parseAndEvaluate(condition, context) : !!defaultValue);

/**
 * 获取复杂标签的键值。
 *
 * @param complexLabelArray - 复杂标签对象数组，包含标签的基本信息。
 * @param context - 可选的上下文对象，用于获取变量类型标签的值。
 * @returns 返回一个新的数组，其中每个标签对象都包含一个转换后的键值（transedKey），
 *          如果标签类型为 'variable'，则根据上下文获取相应的值，否则为一个空字符串。
 */
const getComplexLabelKey = (
  complexLabelArray: IComplexLabelKey[],
  context: any = {},
) =>
  complexLabelArray.map((item) => ({
    ...item,
    transedKey:
      item?.type === 'variable' ? getConditionValue(item.key, context) : '',
  }));

/**
 * 将表单项转换为包含条件和标签的完整对象。
 *
 * @param formItem - 表单项对象，包含各种条件和属性。
 * @param context - 上下文对象，用于计算条件值。
 * @param addtionalCond - 附加条件对象，包含额外的禁用状态。
 * @returns 转换后的表单项对象，包含计算后的显示状态、禁用状态和默认状态等。
 */
const convertFormItem = (
  formItem: IChildProp = {},
  context: any = {},
  addtionalCond: any = {},
): IChildProp => {
  const {
    isDefault = false,
    msgCondition = '',
    condition = '',
    disableCondition = '',
    defaultSelectCondition = '',
    enumKey: originalKey,
    enumTargetType: sourceTypeTarget = 'metric',
    labelKey = '',
  } = formItem || {};
  const targetSourceList: any = SPECIAL_SOURCE_MAP[sourceTypeTarget] || {};
  const targetMetrics = originalKey ? targetSourceList[originalKey] || {} : {};
  // console.log(targetMetrics);
  const trueLabelKey =
    originalKey && targetMetrics ? targetMetrics?.labelKey : labelKey;
  return {
    ...formItem,
    ...targetMetrics,

    labelKey: Array.isArray(trueLabelKey)
      ? getComplexLabelKey(trueLabelKey, context)
      : trueLabelKey,
    isShow: getConditionValue(condition, context, true),
    isDisable: getConditionValue(
      disableCondition,
      context,
      !!addtionalCond?.isDisable,
    ),
    isDefault: getConditionValue(defaultSelectCondition, context, isDefault),
    isMsgShow: getConditionValue(msgCondition, context, false),
  };
};

/**
 * 格式化子项配置，根据父项类型和上下文生成子项数组。
 *
 * @param type - 父项的类型，决定如何格式化子项。
 * @param parentItem - 父项的属性，包含子项和其他相关信息。
 * @param context - 上下文对象，包含额外字段等信息。
 * @param options - 选项对象，包含是否禁用的标志。
 * @returns 格式化后的子项数组。
 */
const ChildrenConfigFormatter = (
  type: IChildProp['type'],
  parentItem: IChildProp,
  context: any,
  { isDisable }: { isDisable: boolean },
): IChildProp[] => {
  const { children = [] } = parentItem || {};
  switch (type) {
    case 'subForm':
      return initFormatter(children, context);
    case 'dynamicText': {
      const dynamicTexts = context?.additionalFields?.dynamicText || '';
      return dynamicTexts.split(',').map((key: string) => ({
        label: key?.trim(),
        value: key?.trim(),
      }));
    }
    default:
      return children.map((child: IChildProp) => {
        // inheritType表示子级会继承父级的type
        if (parentItem.inheritType) {
          Object.assign(child, {
            enumTargetType: parentItem.inheritType,
          });
        }
        if (type === 'checkboxGroup')
          return convertFormItem(child, context, { isDisable });
        if (type === 'cascader') {
          const formattedSecondLevelItem = convertFormItem(child, context);
          return {
            ...formattedSecondLevelItem,
            children: child.children?.map((thirdLevelChild: IChildProp) => {
              Object.assign(thirdLevelChild, {
                enumTargetType: thirdLevelChild?.enumTargetType || 'metric',
              });
              return convertFormItem(thirdLevelChild, context, {
                isDisable: formattedSecondLevelItem.isDisable,
              });
            }),
          };
        }
        return convertFormItem(child, context);
      });
  }
};

/**
 * 初始化格式化函数，用于将配置数组中的每个子项进行格式化。
 *
 * @param config - 待格式化的子项配置数组，默认为空数组。
 * @param context - 上下文对象，默认为空对象。
 * @returns 返回格式化后的子项配置数组。
 *
 * @example
 * const formattedConfig = initFormatter(configArray, contextObject);
 */
export const initFormatter = (
  config: IChildProp[] = [],
  context: any = {},
): IChildProp[] =>
  config.map((parentItem) => {
    const formattedParent = convertFormItem(parentItem, context);
    const { type, isDisable: isParentDisable } = formattedParent;
    return {
      ...formattedParent,
      children: ChildrenConfigFormatter(type, parentItem, context, {
        isDisable: !!isParentDisable,
      }),
    };
  });
